<script setup>
// The setup assistant, running a short believable run on demo data, inside
// /features/setup-assistant. The admin's sentence goes in, the assistant asks
// the one thing that changes the outcome (the REAL SetupQuestionCard), the
// draft fills in and the REAL draft dock pulses "Ready · Review and apply"
// (SetupDraftDock, SetupDraftPanel, SetupDraftFoot). Review and apply opens
// the check view (the REAL SetupStepCard) and Create setup the done view. The
// conversation bubbles are drawn with the conversation page's own classes
// (six test files pin that page's markup, so the bubbles are not a component
// yet); everything they hold is the product's components. utils/setupAssistantDemo
// feeds it; nothing calls the API.
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import SetupDraftDock from '~/components/admin/setup/SetupDraftDock.vue';
import SetupDraftFoot from '~/components/admin/setup/SetupDraftFoot.vue';
import SetupDraftPanel from '~/components/admin/setup/SetupDraftPanel.vue';
import SetupQuestionCard from '~/components/admin/setup/SetupQuestionCard.vue';
import SetupStepCard from '~/components/admin/setup/SetupStepCard.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import {
  SETUP_DEMO_ASK, SETUP_DEMO_CLUB, SETUP_DEMO_CREATED, SETUP_DEMO_CTX, SETUP_DEMO_NEXT, SETUP_DEMO_OPENING, setupDemoCycle, setupDemoProposal, setupDemoQuestion, setupDemoReply,
} from '~/utils/setupAssistantDemo';
import {ASSISTANT_ICON, ASSISTANT_SPEAKER, UNDO_DAYS, UNDO_LINE, applyButtonLabel, createFooter} from '~/utils/setupAssistant';

const {setAlertMessage} = useAlertService();

// ── The run: opening → the admin's sentence → thinking → the question → the pick → the reply and the ready draft ──
const view = ref('chat'); // chat | check | done
const stage = ref(0); // 0 opening, 1 asked, 2 thinking, 3 question, 4 answered, 5 ready
const cycle = ref(null);
const QUESTION = setupDemoQuestion();
const proposal = computed(() => (stage.value >= 1 ? setupDemoProposal({cycle: cycle.value}) : null));
const question = computed(() => (stage.value === 3 ? QUESTION : null));
const typed = ref('');
let timers = [];
const later = (ms, fn) => { timers.push(setTimeout(fn, ms)); };
const clear = () => { timers.forEach(clearTimeout); timers = []; };

// The admin's sentence types itself into the composer, then sends.
const typeAsk = () => {
  typed.value = '';
  let i = 0;
  const tick = () => {
    i += 2;
    typed.value = SETUP_DEMO_ASK.slice(0, i);
    if (i < SETUP_DEMO_ASK.length) later(28, tick);
    else later(500, () => { typed.value = ''; stage.value = 1; later(300, () => { stage.value = 2; later(1400, () => { stage.value = 3; }); }); });
  };
  later(600, tick);
};
const replay = () => {
  clear();
  view.value = 'chat';
  stage.value = 0;
  cycle.value = null;
  typeAsk();
};
onMounted(replay);
onBeforeUnmount(clear);

const onAnswer = ({handles, picked}) => {
  if (stage.value !== 3) return;
  const weeks = parseInt(handles?.[0] ?? '', 10) || (QUESTION.choices.find((c) => c.label === picked?.[0]) ? parseInt(QUESTION.choices.find((c) => c.label === picked?.[0]).handle, 10) : 6);
  cycle.value = setupDemoCycle(weeks);
  stage.value = 4;
  later(1200, () => { stage.value = 5; });
};
const onSomethingElse = () => setAlertMessage({message: 'On the real page you type your own answer in the box below.', color: 'info'});

const time = computed(() => new Date().toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}));
// the latest turn stays in view, as the page's transcript does
const flow = ref(null);
watch(stage, () => nextTick(() => { if (flow.value) flow.value.scrollTop = flow.value.scrollHeight; }));
const reply = computed(() => (cycle.value ? setupDemoReply(cycle.value) : ''));
const applyLabel = computed(() => applyButtonLabel(proposal.value, null));
// the check page's footer, built for THIS draft (a competition open by its link, nothing unpublished), never the constant
const footer = computed(() => createFooter(proposal.value));
const undoUntil = (() => { const d = new Date(); d.setDate(d.getDate() + UNDO_DAYS); return d.toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long'}); })();
const demoOnly = () => setAlertMessage({message: 'This is a demo: the real page does it.', color: 'info'});
const create = () => { view.value = 'done'; };
const inviteLink = 'tapleagues.com/invite/winter-box-league';
</script>

<template>
  <div class="sad">
    <div class="sad-controls">
      <div class="tl-seg" role="group" aria-label="Where in the run">
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': view === 'chat'}" @click="view = 'chat'">Conversation</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': view === 'check'}" :disabled="stage < 5" @click="view = 'check'">Check</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': view === 'done'}" :disabled="stage < 5" @click="view = 'done'">All set</button>
      </div>
      <v-btn variant="text" size="small" prepend-icon="mdi-replay" class="text-none" @click="replay">Replay</v-btn>
    </div>

    <!-- ===== THE CONVERSATION ===== -->
    <div v-if="view === 'chat'" class="tl-card sad-page">
      <div class="sad-head">
        <span class="sad-head-title">Setup assistant <span class="tl-chip">Beta</span></span>
        <span class="sad-head-sub">{{ SETUP_DEMO_CLUB.name }}</span>
      </div>
      <div class="sad-stage">
        <div ref="flow" class="sad-flow">
          <div class="sa-row sa-row--assistant sa-row--run" :class="{'sa-row--end': stage < 3}">
            <img v-if="stage < 3" class="sa-avatar" :src="ASSISTANT_ICON" alt="" width="30" height="30">
            <span v-else class="sa-avatar sa-avatar--gap" aria-hidden="true"></span>
            <div class="sa-turn">
              <div class="sa-name">{{ ASSISTANT_SPEAKER }}</div>
              <div class="sa-bubble sa-bubble--assistant"><div class="sa-text">{{ SETUP_DEMO_OPENING }}</div></div>
            </div>
          </div>

          <div v-if="stage >= 1" class="sa-row sa-row--user sa-row--run sa-row--end">
            <div class="sa-turn sa-turn--user">
              <div class="sa-bubble sa-bubble--user"><div class="sa-text">{{ SETUP_DEMO_ASK }}</div></div>
              <div class="sa-time">{{ time }}</div>
            </div>
            <span class="sa-avatar sa-avatar--me">GC</span>
          </div>

          <div v-if="stage === 2" class="sa-row sa-row--assistant sa-row--run sa-row--end">
            <img class="sa-avatar" :src="ASSISTANT_ICON" alt="" width="30" height="30">
            <div class="sa-turn">
              <div class="sa-name">{{ ASSISTANT_SPEAKER }}</div>
              <div class="sa-bubble sa-bubble--assistant sa-typing" role="status" aria-label="Thinking…">
                <span class="sa-dot"></span><span class="sa-dot"></span><span class="sa-dot"></span>
              </div>
            </div>
          </div>

          <div v-if="stage >= 3" class="sa-row sa-row--assistant sa-row--run" :class="{'sa-row--end': stage === 3}">
            <img v-if="stage === 3" class="sa-avatar" :src="ASSISTANT_ICON" alt="" width="30" height="30">
            <span v-else class="sa-avatar sa-avatar--gap" aria-hidden="true"></span>
            <div class="sa-turn">
              <div class="sa-name">{{ ASSISTANT_SPEAKER }}</div>
              <div class="sa-bubble sa-bubble--assistant">
                <div class="sa-text">A winter box league for squash, singles, boxes of 4. One thing changes the outcome:</div>
                <setup-question-card :question="QUESTION" :answered="stage > 3" class="mt-2" @answer="onAnswer" @something-else="onSomethingElse"/>
              </div>
              <div v-if="stage === 3" class="sa-time">{{ time }}</div>
            </div>
          </div>

          <div v-if="stage >= 4" class="sa-row sa-row--user sa-row--run sa-row--end">
            <div class="sa-turn sa-turn--user">
              <div class="sa-bubble sa-bubble--user"><div class="sa-text">{{ cycle?.label }}</div></div>
              <div class="sa-time">{{ time }}</div>
            </div>
            <span class="sa-avatar sa-avatar--me">GC</span>
          </div>

          <div v-if="stage === 4" class="sa-row sa-row--assistant sa-row--run sa-row--end">
            <img class="sa-avatar" :src="ASSISTANT_ICON" alt="" width="30" height="30">
            <div class="sa-turn">
              <div class="sa-name">{{ ASSISTANT_SPEAKER }}</div>
              <div class="sa-bubble sa-bubble--assistant sa-typing" role="status" aria-label="Thinking…">
                <span class="sa-dot"></span><span class="sa-dot"></span><span class="sa-dot"></span>
              </div>
            </div>
          </div>

          <div v-if="stage >= 5" class="sa-row sa-row--assistant sa-row--run sa-row--end">
            <img class="sa-avatar" :src="ASSISTANT_ICON" alt="" width="30" height="30">
            <div class="sa-turn">
              <div class="sa-name">{{ ASSISTANT_SPEAKER }}</div>
              <div class="sa-bubble sa-bubble--assistant"><div class="sa-text">{{ reply }}</div></div>
              <div class="sa-time">{{ time }}</div>
            </div>
          </div>
          <div class="sad-flow-room" aria-hidden="true"/>
        </div>

        <!-- The real draft dock: collapsed bar (pulsing once the draft is ready), the real panel and foot inside -->
        <client-only>
          <setup-draft-dock :visible="stage >= 1" :proposal="proposal" :question="question" :notes-count="0">
            <template #default="{collapse}">
              <setup-draft-panel :proposal="proposal" :payments="null" :question="question" @change="demoOnly" @answer="collapse">
                <template #head-end>
                  <tl-icon-btn tooltip="Collapse the draft" icon="mdi-chevron-down" @click="collapse"/>
                </template>
              </setup-draft-panel>
            </template>
            <template #foot="{collapse}">
              <setup-draft-foot :proposal="proposal" :availability="null" @check="collapse(); view = 'check'"/>
            </template>
          </setup-draft-dock>
        </client-only>
      </div>
      <div class="sad-composer">
        <div class="sad-input" :class="{'sad-input--lit': stage === 0 && typed}">
          <span v-if="typed" class="sad-typed">{{ typed }}<span class="sad-caret" aria-hidden="true"/></span>
          <span v-else class="sad-placeholder">Type your answer, or paste a price list…</span>
        </div>
        <span class="sad-send" aria-hidden="true"><v-icon size="20" icon="mdi-arrow-up" color="white"/></span>
      </div>
    </div>

    <!-- ===== CHECK BEFORE CREATING ===== -->
    <div v-else-if="view === 'check'" class="tl-card sad-page sad-check">
      <div class="sad-head">
        <span class="sad-head-title">Check before creating <span class="tl-chip">Beta</span></span>
        <span class="sad-head-sub">1 change · drafted by you, today</span>
      </div>
      <section class="sad-section">
        <div class="tl-section-head">
          <h2>Competitions · 1</h2>
          <span class="sad-section-note">Open to joining by its invite link once created</span>
        </div>
        <setup-step-card v-if="proposal" :step="proposal.steps[0]" :ctx="SETUP_DEMO_CTX" @edit="demoOnly" @remove="demoOnly" @open-page="demoOnly"/>
      </section>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Checks</h2></div>
        <div class="tl-card sad-check-card">
          <div class="sad-check-ok"><v-icon size="16" icon="mdi-check-circle" color="success"/>Passes the same checks as the forms: names, prices, ages, dates and booking hours.</div>
        </div>
      </section>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Things I assumed</h2></div>
        <div class="tl-card sad-assumed">
          <div v-for="a in proposal?.assumptions ?? []" :key="a.id" class="sad-assumed-row">
            <span>{{ a.text }}</span>
            <v-btn variant="text" size="small" class="text-none" @click="view = 'chat'">Change</v-btn>
          </div>
        </div>
      </section>
      <div class="sad-apply">
        <p class="sad-apply-note">{{ footer }}</p>
        <v-btn color="primary" size="large" rounded="pill" class="text-none" @click="create">{{ applyLabel }}</v-btn>
      </div>
    </div>

    <!-- ===== ALL SET ===== -->
    <div v-else class="tl-card sad-page sad-done">
      <div class="sad-head">
        <span class="sad-head-title">All set <span class="tl-chip">Beta</span></span>
        <span class="sad-head-sub">{{ SETUP_DEMO_CLUB.name }}</span>
      </div>
      <div class="tl-card sad-hero">
        <div class="sad-hero-ic"><v-icon size="24" icon="mdi-check-circle-outline"/></div>
        <div class="flex-fill">
          <div class="sad-hero-t">Your setup is created</div>
          <div class="sad-hero-d">Applied today: 1 change. Players can join the Winter Box League by its invite link.</div>
        </div>
        <span class="tl-chip tl-chip--ok">Done</span>
      </div>
      <div class="sad-done-main">
        <v-btn color="primary" size="large" rounded="pill" class="text-none" @click="view = 'chat'">Done</v-btn>
      </div>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Share the link</h2></div>
        <div class="tl-card sad-link">
          <span class="sad-link-ic"><v-icon size="18" icon="mdi-table"/></span>
          <div class="flex-fill">
            <div class="sad-link-t">Winter Box League</div>
            <div class="sad-link-d">Joins the club and the box league. {{ inviteLink }}</div>
          </div>
          <div class="sad-link-actions">
            <tl-icon-btn tooltip="Copy link" icon="mdi-content-copy" @click="demoOnly"/>
            <tl-icon-btn tooltip="Share" icon="mdi-share-variant-outline" @click="demoOnly"/>
            <tl-icon-btn tooltip="Printable poster (PDF)" icon="mdi-qrcode" @click="demoOnly"/>
            <tl-icon-btn tooltip="Open its page" icon="mdi-open-in-new" @click="demoOnly"/>
          </div>
        </div>
        <ul class="sad-created">
          <li v-for="line in SETUP_DEMO_CREATED" :key="line">{{ line }}</li>
        </ul>
      </section>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Next steps</h2></div>
        <div class="tl-card sad-next">
          <div v-for="n in SETUP_DEMO_NEXT" :key="n.n" class="sad-next-row">
            <span class="sad-next-n">{{ n.n }}</span>
            <span class="flex-fill">{{ n.text }}</span>
            <v-btn variant="tonal" color="primary" size="small" rounded="pill" class="text-none" @click="demoOnly">{{ n.button }}</v-btn>
          </div>
        </div>
      </section>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Want to change something?</h2></div>
        <div class="sad-change">
          <v-btn variant="tonal" color="primary" rounded="pill" class="text-none" prepend-icon="mdi-creation-outline" @click="replay">Change with the assistant</v-btn>
          <v-btn variant="outlined" rounded="pill" class="text-none" @click="demoOnly">Your competitions</v-btn>
        </div>
      </section>
      <section class="sad-section">
        <div class="tl-section-head"><h2>Undo</h2></div>
        <div class="tl-card sad-undo">
          <p>{{ UNDO_LINE }} <span class="sad-undo-until">Available until {{ undoUntil }}.</span></p>
          <v-btn variant="outlined" color="error" rounded="pill" class="text-none" @click="demoOnly">Undo this setup</v-btn>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sad-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin-bottom: 14px;

  .tl-seg__opt:disabled {
    opacity: 0.45;
    cursor: default;
  }

  /* three pills never push a phone sideways */
  .tl-seg {
    max-width: 100%;
    flex-wrap: wrap;
  }
}

.sad-page {
  padding: 0;
  overflow: hidden;
}

.sad-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.sad-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.2px;
}

.sad-head-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

/* the transcript stage: a fixed height like the page's card, the conversation scrolling inside it, the dock floating
   inside it (position: relative, as the page's stage) */
.sad-stage {
  position: relative;
  height: 560px;
  padding: 0;
  background: rgb(var(--v-theme-light));
  overflow: hidden;

  @media (max-width: 599.98px) {
    height: 520px;
  }

  /* the open draft stays inside the stage frame (the real page pins it to the viewport, over its own page): the dock's
     root spans the stage so the panel's height is measured against it, and the bar keeps its bottom-right corner */
  :deep(.sdd) {
    position: absolute;
    inset: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  :deep(.sdd > *) {
    pointer-events: auto;
  }

  :deep(.sdd-bar) {
    position: absolute;
    right: 8px;
    bottom: 8px;
  }

  :deep(.sdd-panel) {
    position: absolute;
    right: 8px;
    bottom: 8px;
    left: auto;
    top: auto;
    width: min(440px, calc(100% - 16px));
    max-height: calc(100% - 16px);
  }

  :deep(.sdd-scrim) {
    position: absolute;
  }
}

.sad-flow {
  display: flex;
  flex-direction: column;
  gap: 3px;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

/* room under the last bubble so the collapsed draft bar never covers it */
.sad-flow-room {
  height: 72px;
}

/* ── the conversation page's own bubble classes (pages/admin/clubs/[clubId]/setup-assistant/index.vue) ── */
.sa-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  &--user { justify-content: flex-end; }
  &--run { margin-top: 11px; }
  &--run:first-child { margin-top: 0; }
}

.sa-row--end:has(.sa-time) .sa-avatar { margin-bottom: 18px; }

.sa-avatar {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: #fff;
  object-fit: cover;
  display: block;
  overflow: hidden;
  font-size: 0;

  &--gap { border: 0; background: transparent; }

  &--me {
    border: 0;
    background: rgb(var(--v-theme-primary));
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.sa-turn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  max-width: min(85%, calc(100% - 38px));

  @media (max-width: 959px) {
    max-width: min(90%, calc(100% - 38px));
  }

  &--user { align-items: flex-end; }
}

.sa-name {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: .2px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.2;
  margin: 0 0 4px 4px;
}

.sa-bubble {
  max-width: 100%;
  border-radius: 16px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.55;

  &--user {
    background: rgb(var(--v-theme-primary));
    color: #fff;
    border-bottom-right-radius: 6px;
  }

  &--assistant {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-borderLight));
    color: rgb(var(--v-theme-text));
    border-bottom-left-radius: 6px;
  }
}

.sa-text { white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }

.sa-time {
  height: 18px;
  line-height: 18px;
  font-size: 10.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  margin: 0 4px;
}

.sa-typing {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 14px 16px;
}

.sa-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgb(var(--v-theme-textSubtle));
  opacity: 0.35;
  animation: sad-bounce 1.2s ease-in-out infinite;

  &:nth-child(2) { animation-delay: 0.15s; }
  &:nth-child(3) { animation-delay: 0.3s; }
}

@keyframes sad-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30% { transform: translateY(-4px); opacity: 0.9; }
}

/* the composer: the page's pill, a borderless field, the round brand send button */
.sad-composer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 12px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
}

.sad-input {
  flex: 1;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-light));
  font-size: 14px;

  &--lit {
    border-color: rgb(var(--v-theme-primary));
    background: rgb(var(--v-theme-surface));
  }
}

.sad-placeholder {
  color: rgb(var(--v-theme-textSubtle));
}

.sad-typed {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sad-caret {
  display: inline-block;
  width: 1px;
  height: 16px;
  margin-left: 1px;
  vertical-align: -3px;
  background: rgb(var(--v-theme-text));
  animation: sad-caret 1s steps(2) infinite;
}

@keyframes sad-caret {
  to { opacity: 0; }
}

.sad-send {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-primary));
}

@media (prefers-reduced-motion: reduce) {
  .sa-dot { animation: none; opacity: 0.6; }
  .sad-caret { animation: none; }
}

/* ── the check and done views ── */
.sad-check,
.sad-done {
  padding-bottom: 16px;
}

.sad-section {
  padding: 4px 16px 0;
  margin-top: 10px;
}

.sad-section-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.sad-check-card {
  padding: 12px 14px;
}

.sad-check-ok {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13.5px;
  line-height: 1.5;
}

.sad-assumed {
  padding: 0;
  overflow: hidden;
}

.sad-assumed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 8px 8px 14px;
  font-size: 13.5px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-child { border-top: 0; }
}

.sad-apply {
  margin: 16px 16px 0;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sad-apply-note {
  flex: 1 1 320px;
  margin: 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: rgb(var(--v-theme-textSubtle));
}

.sad-hero {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 16px 0;
  padding: 14px;
  border-left: 4px solid rgb(var(--v-theme-success));
}

.sad-hero-ic {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
}

.sad-hero-t { font-size: 15px; font-weight: 800; }
.sad-hero-d { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; }

.sad-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
}

.sad-link-ic {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.sad-link-t { font-size: 14px; font-weight: 700; }
.sad-link-d { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); overflow-wrap: anywhere; }
.sad-link-actions { display: flex; gap: 2px; }

.sad-created {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 13.5px;
  line-height: 1.6;
}

.sad-next { padding: 0; overflow: hidden; }

.sad-next-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  font-size: 13.5px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-child { border-top: 0; }
}

.sad-next-n {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 800;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.sad-done-main {
  padding: 12px 16px 0;
}

.sad-change {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sad-undo-until {
  font-weight: 700;
}

.sad-undo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;

  p {
    flex: 1 1 320px;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: rgb(var(--v-theme-charcoal));
  }
}
</style>
